import { Plus } from 'lucide-react';
import { SLOT_LABEL, STATUS_LABEL, formatDay } from '@nu/shared';
import { COUNTS, LEAVE_LABEL, SLOT_SHORT, addDays, chipTitle, fillLevel, mondayOf, shortName, slotsText, slotsUsed } from './shared.jsx';

// Month at a glance. With all teams: each day shows every team's load
// against its daily target, so empty and overbooked days stand out; click a
// day to open its week. With one team: that team's bookings in each day.
// Either way, team members on holiday are shown on the days they're off.

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const SLOT_ORDER = { am: 0, all_day: 1, pm: 2 };

export function monthGrid(monthStart) {
  const [y, m] = monthStart.split('-').map(Number);
  const lastDay = new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10);
  const first = mondayOf(monthStart);
  const last = addDays(mondayOf(lastDay), 6);
  const weeks = [];
  for (let w = first; w <= last; w = addDays(w, 7)) weeks.push(DAY_NAMES.map((_, i) => addDays(w, i)));
  return { first, last, weeks };
}

export default function BookingsMonth({ monthStart, today, bookings, teams, teamFilter, people = [], leave = [], colours = new Map(), canEdit, onOpen, onNew, onPickDay }) {
  const { weeks } = monthGrid(monthStart);
  const month = monthStart.slice(0, 7);
  const nameOf = new Map(people.map((p) => [p.id, p.name]));
  const oneTeam = teamFilter && teamFilter !== 'none' ? teams.find((t) => String(t.id) === String(teamFilter)) : null;
  const shownTeams = oneTeam ? [oneTeam] : teams.filter((t) => t.active);

  const onDay = (date) => bookings.filter((b) => b.bookingDate === date);
  const offOn = (date) => shownTeams.flatMap((t) => (t.members || [])
    .filter((m) => m.fromDate <= date && (!m.toDate || m.toDate >= date))
    .map((m) => ({ m, l: leave.find((x) => x.personId === m.personId && x.day === date) }))
    .filter((x) => x.l)
    .map(({ m, l }) => ({ id: m.personId, name: nameOf.get(m.personId) || 'Someone', part: l.part, teamId: t.id })));

  return (
    <div className="table-scroll">
      <table className="cal cal-month">
        <thead>
          <tr>{DAY_NAMES.map((d) => <th key={d}>{d}</th>)}</tr>
        </thead>
        <tbody>
          {weeks.map((week) => (
            <tr key={week[0]}>
              {week.map((date) => {
                const list = onDay(date);
                const off = offOn(date);
                const outside = date.slice(0, 7) !== month;
                const unassigned = list.filter((b) => !b.teamId && COUNTS(b)).length;
                return (
                  <td key={date} className={`cal-cell month-cell${date === today ? ' cal-today' : ''}${outside ? ' outside' : ''}`}>
                    <button type="button" className="month-day" onClick={() => onPickDay(date)} title={`Open the week of ${formatDay(date)}`}>
                      {Number(date.slice(8))}
                    </button>

                    {!oneTeam && shownTeams.map((t) => {
                      const used = slotsUsed(list.filter((b) => b.teamId === t.id));
                      const target = t.target?.value ?? null;
                      const fill = fillLevel(used, target);
                      const state = fill ? ` fill-${fill}` : ' empty';
                      return (
                        <div key={t.id} className={`month-load${state}`} style={{ '--team': colours.get(t.id) }}>
                          <span className="month-team">{t.name}</span>
                          <span>{slotsText(used)}{target !== null ? ` / ${target}` : ''}</span>
                        </div>
                      );
                    })}
                    {!oneTeam && unassigned > 0 && <div className="month-load unassigned"><span className="month-team">Not on a team</span><span>{unassigned}</span></div>}

                    {off.map((o) => (
                      <div key={`${o.teamId}-${o.id}`} className={`cal-leave leave-${o.part}`} title={`${o.name} is on holiday${o.part === 'all' ? '' : ` (${o.part.toUpperCase()})`}`}>
                        {shortName(o.name)} {LEAVE_LABEL[o.part]}
                      </div>
                    ))}

                    {oneTeam && list
                      .filter((b) => b.teamId === oneTeam.id)
                      .sort((a, b) => (SLOT_ORDER[a.slot] ?? 3) - (SLOT_ORDER[b.slot] ?? 3) || a.id - b.id)
                      .map((b) => (
                        <button key={b.id} type="button" className={`cal-chip compact status-${b.status}${b.revisitOf ? ' cal-revisit' : ''}`} onClick={() => onOpen(b.id)}
                          title={chipTitle(b)}>
                          <span className="chip-slot">{SLOT_SHORT[b.slot] || '–'}</span> {b.reference}
                        </button>
                      ))}
                    {oneTeam && (() => {
                      const used = slotsUsed(list.filter((b) => b.teamId === oneTeam.id));
                      const target = oneTeam.target?.value ?? null;
                      return target !== null && (used > 0 || canEdit) ? <div className={`cal-load${used > target ? ' over' : used >= target ? ' full' : ''}`}>{slotsText(used)} / {target}</div> : null;
                    })()}
                    {oneTeam && canEdit && !outside && (
                      <button type="button" className="cal-add" onClick={() => onNew({ teamId: oneTeam.id, bookingDate: date })} aria-label={`Book ${oneTeam.name} on ${formatDay(date)}`}>
                        <Plus size={16} aria-hidden="true" />
                      </button>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
